/**
 * Carnet de route de la camionnette — menu Livraisons Clients (26/10/2026).
 *
 * Les trajets sont notés dans l'app livreur (compteur départ/arrivée, destination,
 * motif). Ici : le mois, les km totaux et ceux des livraisons, les km NON notés
 * entre deux trajets (ce qu'un contrôle relèverait), la correction d'une faute de
 * frappe, et l'export CSV pour le comptable.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

interface Trajet {
  id: string;
  depart_at: string;
  depart_non_note?: boolean;
  arrivee_at?: string | null;
  compteur_depart: number;
  compteur_arrivee?: number | null;
  destination: string;
  motif: string;
  conducteur_nom?: string | null;
  notes?: string | null;
}
interface Periode {
  trajets: Trajet[];
  total_km: number;
  km_livraison: number;
  km_non_notes: Array<{ apres: string; avant: string; km: number }>;
}

const MOTIFS: Record<string, string> = {
  livraison: 'Livraison clients',
  fournisseur: 'Fournisseur / achats',
  marche: 'Marché',
  administratif: 'Banque / administratif',
  autre: 'Autre',
};

const moisCourant = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const date = (iso: string) => new Date(iso).toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'short' });
const heure = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' }) : '—');

export default function CarnetRoutePage() {
  const [mois, setMois] = useState(moisCourant);
  const [periode, setPeriode] = useState<Periode | null>(null);
  const [erreur, setErreur] = useState('');
  const [edition, setEdition] = useState<Trajet | null>(null);

  const charger = async () => {
    setErreur('');
    try {
      setPeriode(await api.get<Periode>(`/customer-deliveries/carnet?mois=${mois}`));
    } catch (e: any) {
      setErreur(e?.response?.data?.message || e?.message || 'Carnet illisible');
    }
  };
  useEffect(() => {
    void charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mois]);

  const trouApres = useMemo(() => new Map((periode?.km_non_notes || []).map((t) => [t.apres, t.km])), [periode]);

  const exporter = async () => {
    const { filename, csv } = await api.get<{ filename: string; csv: string }>(`/customer-deliveries/carnet/export?mois=${mois}`);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const enregistrer = async () => {
    if (!edition) return;
    try {
      await api.patch(`/customer-deliveries/carnet/${edition.id}`, {
        compteur_depart: edition.compteur_depart,
        compteur_arrivee: edition.compteur_arrivee ?? null,
        destination: edition.destination,
        motif: edition.motif,
        notes: edition.notes ?? '',
      });
      setEdition(null);
      await charger();
    } catch (e: any) {
      setErreur(e?.response?.data?.message || e?.message || 'Correction refusée');
    }
  };

  const nonNotes = (periode?.km_non_notes || []).reduce((s, t) => s + t.km, 0);

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Carnet de route</h1>
          <p className="mt-2 text-gray-600">Camionnette — trajets notés dans l'app livreur (compteur, destination, motif).</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="month"
            value={mois}
            onChange={(e) => e.target.value && setMois(e.target.value)}
            className="min-h-[44px] rounded-lg border border-gray-300 px-3"
            aria-label="Mois"
          />
          <button onClick={exporter} className="min-h-[44px] px-4 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700">
            Exporter (CSV)
          </button>
        </div>
      </div>

      {erreur && <p className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-800">{erreur}</p>}

      {periode && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Chiffre libelle="Trajets" valeur={String(periode.trajets.length)} />
            <Chiffre libelle="Km notés" valeur={`${periode.total_km} km`} />
            <Chiffre libelle="dont livraisons" valeur={`${periode.km_livraison} km`} />
            <Chiffre libelle="Km non notés" valeur={`${nonNotes} km`} alerte={nonNotes > 0} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Départ → arrivée</th>
                  <th className="px-3 py-2 text-right">Compteur</th>
                  <th className="px-3 py-2 text-right">Km</th>
                  <th className="px-3 py-2">Destination</th>
                  <th className="px-3 py-2">Motif</th>
                  <th className="px-3 py-2">Conducteur</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {periode.trajets.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-gray-500">Aucun trajet ce mois-ci.</td>
                  </tr>
                )}
                {periode.trajets.map((t) => (
                  <Fragment key={t.id}>
                    <tr>
                      <td className="px-3 py-2 whitespace-nowrap">{date(t.depart_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{t.depart_non_note ? <span className="text-amber-800">départ non noté</span> : heure(t.depart_at)} → {heure(t.arrivee_at)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                        {t.compteur_depart} → {t.compteur_arrivee ?? '…'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {t.compteur_arrivee != null ? t.compteur_arrivee - t.compteur_depart : 'en cours'}
                      </td>
                      <td className="px-3 py-2">{t.destination}{t.notes ? <span className="block text-xs text-gray-500">{t.notes}</span> : null}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{MOTIFS[t.motif] ?? t.motif}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{t.conducteur_nom ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setEdition(t)} className="min-h-[36px] px-3 rounded-lg border border-gray-300 text-gray-700">
                          Corriger
                        </button>
                      </td>
                    </tr>
                    {trouApres.has(t.id) && (
                      <tr className="bg-amber-50">
                        <td colSpan={8} className="px-3 py-2 text-amber-900 font-medium">
                          ⚠️ {trouApres.get(t.id)} km non notés avant le trajet suivant (le compteur a bougé sans trajet inscrit)
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {edition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Corriger le trajet">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Corriger le trajet du {date(edition.depart_at)}</h2>
            <div className="grid grid-cols-2 gap-3">
              <Champ libelle="Compteur départ" valeur={String(edition.compteur_depart)} onChange={(v) => setEdition({ ...edition, compteur_depart: Number(v.replace(/\D/g, '')) })} chiffres />
              <Champ
                libelle="Compteur arrivée"
                valeur={edition.compteur_arrivee != null ? String(edition.compteur_arrivee) : ''}
                onChange={(v) => setEdition({ ...edition, compteur_arrivee: v.replace(/\D/g, '') ? Number(v.replace(/\D/g, '')) : null })}
                chiffres
              />
            </div>
            <Champ libelle="Destination" valeur={edition.destination} onChange={(v) => setEdition({ ...edition, destination: v })} />
            <label className="block text-sm font-medium text-gray-700">
              Motif
              <select
                value={edition.motif}
                onChange={(e) => setEdition({ ...edition, motif: e.target.value })}
                className="mt-1 w-full min-h-[44px] rounded-lg border border-gray-300 px-3"
              >
                {Object.entries(MOTIFS).map(([code, libelle]) => (
                  <option key={code} value={code}>{libelle}</option>
                ))}
              </select>
            </label>
            <Champ libelle="Remarque (raison de la correction)" valeur={edition.notes ?? ''} onChange={(v) => setEdition({ ...edition, notes: v })} />
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEdition(null)} className="min-h-[44px] px-4 rounded-lg border border-gray-300">Annuler</button>
              <button
                onClick={async () => {
                  if (!window.confirm('Supprimer ce trajet ? À réserver à une saisie en double : un trajet réel supprimé laisse des km non notés.')) return;
                  try {
                    await api.delete(`/customer-deliveries/carnet/${edition.id}`);
                    setEdition(null);
                    await charger();
                  } catch (e: any) {
                    setErreur(e?.response?.data?.message || e?.message || 'Suppression refusée');
                  }
                }}
                className="min-h-[44px] px-4 rounded-lg border border-red-300 text-red-700"
              >
                Supprimer
              </button>
              <button onClick={enregistrer} className="flex-1 min-h-[44px] rounded-lg bg-blue-600 text-white font-medium">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Chiffre({ libelle, valeur, alerte }: { libelle: string; valeur: string; alerte?: boolean }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${alerte ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
      <div className="text-sm text-gray-600">{libelle}</div>
      <div className={`text-2xl font-bold tabular-nums ${alerte ? 'text-amber-900' : 'text-gray-900'}`}>{valeur}</div>
    </div>
  );
}

function Champ({ libelle, valeur, onChange, chiffres }: { libelle: string; valeur: string; onChange: (v: string) => void; chiffres?: boolean }) {
  return (
    <label className="block text-sm font-medium text-gray-700">
      {libelle}
      <input
        type="text"
        inputMode={chiffres ? 'numeric' : undefined}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full min-h-[44px] rounded-lg border border-gray-300 px-3"
      />
    </label>
  );
}
